import React, { useState, useMemo, useEffect } from 'react';
import { useShift, SHIFT_OPTIONS, ALL_SHIFT_OPTIONS, shiftOptionsForDate } from '../../context/ShiftContext';
import { useShiftI18n } from '../../i18n/ShiftI18nContext';

const ROLE_PILL = { staff: 'rp-staff', early: 'rp-early', late: 'rp-late', night: 'rp-late', maintenance: 'rp-maint', all: 'rp-owner' };
/** Chinese defaults kept for non-React callers / legacy export */
const SHIFT_LABEL = { early: '早班', late: '晚班', night: '夜班', maintenance: '維護班' };

const THEME_KEY = 'shift_theme';

export function getShiftTheme() {
  try {
    const t = localStorage.getItem(THEME_KEY);
    return t === 'light' ? 'light' : 'dark';
  } catch {
    return 'dark';
  }
}

export function setShiftTheme(theme) {
  const next = theme === 'light' ? 'light' : 'dark';
  try { localStorage.setItem(THEME_KEY, next); } catch { /* ignore */ }
  if (typeof document !== 'undefined') {
    document.querySelectorAll('.shift-root').forEach((el) => {
      el.setAttribute('data-theme', next);
    });
  }
  return next;
}

export function roleVisual(mode, role, t) {
  const labelOr = (key, fallback) => (typeof t === 'function' ? t(key) : fallback);
  if (role === 'owner') {
    const shiftKey = mode && mode !== 'all' ? `shift.${mode}` : null;
    return {
      pill: ROLE_PILL[mode] || 'rp-owner',
      label: shiftKey && typeof t === 'function'
        ? t(shiftKey)
        : (SHIFT_LABEL[mode] || labelOr('role.ownerPill', 'Nana')),
    };
  }
  if (role === 'supervisor') {
    const shiftKey = mode && SHIFT_LABEL[mode] ? `shift.${mode}` : null;
    return {
      pill: ROLE_PILL[mode] || 'rp-early',
      label: shiftKey && typeof t === 'function'
        ? t(shiftKey)
        : labelOr('role.supervisor', '小主管'),
    };
  }
  return { pill: 'rp-staff', label: labelOr('role.staff', '員工') };
}

export function ShiftTabBadge({ count }) {
  const n = Number(count) || 0;
  if (n <= 0) return null;
  return <span className="shift-tab-badge">{n > 99 ? '99+' : n}</span>;
}

export function TopBar({ title, sub, onBack, rightPill }) {
  const { t } = useShiftI18n();
  return (
    <div className="shift-topbar">
      {onBack && (
        <button className="shift-back" onClick={onBack} aria-label={t('common.back')}>‹</button>
      )}
      <div className="shift-tb-info">
        <div className="shift-tb-title">{title}</div>
        {sub && <div className="shift-tb-sub">{sub}</div>}
      </div>
      {rightPill && <span className={`shift-rp ${rightPill.cls}`}>{rightPill.text}</span>}
    </div>
  );
}

/** 主管／老闆：頂欄內嵌檢視班別（點開再選） */
export function SupModePicker({ open, onToggle }) {
  const { user, supModes, setSupMode, availableShifts, bizDate } = useShift();
  const { t } = useShiftI18n();
  if (!user || user.role === 'staff') return null;
  const allowedIds = (availableShifts?.length
    ? availableShifts
    : shiftOptionsForDate(bizDate).map((s) => s.id));
  const modes = ALL_SHIFT_OPTIONS.filter((m) => allowedIds.includes(m.id));
  if (!modes.length) return null;
  const current = (supModes && supModes[0]) || modes[0].id;
  const label = t(`shift.${current}`) || current;
  return (
    <div className="shift-sup-mode-wrap">
      <button
        type="button"
        className={`shift-tb-chip ${open ? 'on' : ''}`}
        onClick={onToggle}
        aria-expanded={open}
      >
        {t('chrome.viewMode', { label })}
      </button>
      {open ? (
        <div className="shift-tb-shift-edit shift-sup-mode-panel">
          {modes.map((m) => (
            <button
              key={m.id}
              type="button"
              className={`shift-mb-btn ${current === m.id ? 'on' : ''}`}
              onClick={() => { setSupMode(m.id); onToggle(false); }}
            >{t(`shift.${m.id}`)}</button>
          ))}
        </div>
      ) : null}
    </div>
  );
}

/** @deprecated 改用頂欄 SupModePicker；保留避免舊引用炸掉 */
export function ModeBar() {
  return null;
}

/** 頂欄：日期／姓名／班別／進度％／編輯班別／主題／語系 */
export function ShiftHeaderBar({
  showSupMode = false,
  currentTabLabel,
}) {
  const {
    user, activeShift, bizDate, progress, extraProgress,
    changeShift, logout, isStaff, isOwner, availableShifts,
  } = useShift();
  const { lang, setLang, t } = useShiftI18n();
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [supOpen, setSupOpen] = useState(false);
  const [theme, setThemeState] = useState(() => getShiftTheme());
  const pct = progress?.pct || 0;
  const extraLeft = Math.max(0, (extraProgress?.total || 0) - (extraProgress?.done || 0));
  const options = useMemo(() => {
    if (availableShifts?.length) return ALL_SHIFT_OPTIONS.filter((s) => availableShifts.includes(s.id));
    return shiftOptionsForDate(bizDate);
  }, [availableShifts, bizDate]);

  useEffect(() => {
    setShiftTheme(theme);
  }, [theme]);

  const toggleTheme = () => {
    setThemeState((th) => setShiftTheme(th === 'light' ? 'dark' : 'light'));
  };

  const toggleLang = () => {
    setLang(lang === 'en' ? 'zh-Hant' : 'en');
  };

  const applyShift = async (st) => {
    setSaving(true);
    try {
      await changeShift(st);
      setEditing(false);
    } catch {
      alert(t('chrome.shiftUpdateFail'));
    } finally {
      setSaving(false);
    }
  };

  const canEditOwnShift = !isOwner;

  return (
    <div className="shift-topbar shift-topbar-compact">
      <div className="shift-tb-row">
        <div className="shift-tb-info">
          <div className="shift-tb-title">{bizDate || t('common.today')} · {user?.name || ''}</div>
          <div className="shift-tb-sub">
            <span className="shift-sync-dot" />
            {isOwner ? t('role.ownerView') : (activeShift ? t(`shift.${activeShift}`) : t('chrome.noShift'))}
            {user?.role === 'supervisor' ? t('chrome.supervisorSuffix') : ''}
            {currentTabLabel ? ` · ${currentTabLabel}` : ''}
          </div>
        </div>
        <div className="shift-tb-actions">
          <button
            type="button"
            className="shift-tb-chip"
            onClick={toggleLang}
            title={`${t('lang.zh')} / ${t('lang.en')}`}
          >
            {lang === 'en' ? t('lang.zh') : t('lang.en')}
          </button>
          <button type="button" className="shift-tb-chip" onClick={toggleTheme} title={t('chrome.themeTitle')}>
            {theme === 'light' ? t('chrome.themeDark') : t('chrome.themeLight')}
          </button>
          {canEditOwnShift && (
            <button
              type="button"
              className="shift-rp rp-staff"
              style={{ border: 'none', cursor: 'pointer' }}
              onClick={() => { setEditing((v) => !v); setSupOpen(false); }}
            >
              {t('chrome.changeShift')}
            </button>
          )}
          <button type="button" className="shift-logout-link" onClick={logout}>{t('common.logout')}</button>
        </div>
      </div>
      {showSupMode ? (
        <SupModePicker
          open={supOpen}
          onToggle={(v) => {
            if (typeof v === 'boolean') setSupOpen(v);
            else { setSupOpen((o) => !o); setEditing(false); }
          }}
        />
      ) : null}
      {isStaff ? (
        <div className="shift-tb-prog">
          <div className="shift-tb-prog-bar">
            <div style={{
              width: `${pct}%`, height: '100%', borderRadius: 999,
              background: pctColor(pct),
              transition: 'width .25s ease',
            }} />
          </div>
          <span className="shift-tb-pct" style={{ color: pctColor(pct) }}>{pct}%</span>
          {extraLeft > 0 && (
            <span className="shift-tb-extra-hint">{t('chrome.extraLeft', { n: extraLeft })}</span>
          )}
        </div>
      ) : (
        <div className="shift-tb-sub" style={{ marginTop: 2 }}>{t('chrome.progressHint')}</div>
      )}
      {editing && canEditOwnShift && (
        <div className="shift-tb-shift-edit">
          {options.map((s) => (
            <button
              key={s.id}
              type="button"
              disabled={saving}
              className={`shift-mb-btn ${activeShift === s.id ? 'on' : ''}`}
              onClick={() => applyShift(s.id)}
            >{t(`shift.${s.id}`)}</button>
          ))}
        </div>
      )}
    </div>
  );
}

export function pctColor(p) {
  const n = Number(p) || 0;
  if (n <= 0) return 'var(--text3)';
  if (n <= 30) return 'var(--danger)';
  if (n <= 60) return 'var(--warning)';
  if (n <= 80) return 'var(--accent)';
  return 'var(--success)';
}
export function dotClass(p) {
  const n = Number(p) || 0;
  if (n <= 0) return 'dot-off';
  if (n <= 30) return 'dot-off';
  if (n <= 60) return 'dot-pt';
  return 'dot-on';
}

export { SHIFT_OPTIONS, SHIFT_LABEL };
